import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAttachmentUploads } from "@/lib/attachments";
import type { RichDoc } from "@/lib/board";

import { RichCommentEditor } from "./RichCommentEditor";

export type RequestChangesPayload = {
  feedback: string;
  rich: RichDoc | null;
  attachmentIds: string[];
};

/**
 * The single "Changes requested" feedback form — a larger modal with the rich
 * editor + drag-and-drop / paste attachments. Shared by BOTH entry points (the
 * task-detail "Request changes" button and the board's drag-to-changes-requested
 * confirmation), so there's one composer to maintain. It owns its own draft
 * state (text, rich doc, pending uploads) and resets whenever it opens or the
 * task changes; the caller just runs the mutation in `onSubmit`.
 */
export function RequestChangesDialog({
  open,
  taskId,
  taskTitle,
  submitting,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  taskId: string | null;
  taskTitle: string;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (payload: RequestChangesPayload) => void;
}) {
  const [feedback, setFeedback] = useState("");
  const [rich, setRich] = useState<RichDoc | null>(null);
  const { items, add, remove, reset, doneIds, uploading } =
    useAttachmentUploads(taskId);

  // Fresh form each time it opens (and clear leftovers when it closes or a
  // different task is staged) — the editor itself remounts via its `key`.
  useEffect(() => {
    setFeedback("");
    setRich(null);
    reset();
  }, [open, taskId, reset]);

  const canSubmit = feedback.trim().length > 0 && !uploading && !submitting;

  function submit() {
    if (canSubmit) {
      onSubmit({ feedback: feedback.trim(), rich, attachmentIds: doneIds });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !submitting) onCancel();
      }}
    >
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-2xl">
        <DialogHeader className="space-y-1.5 border-b border-border px-6 py-4 text-left">
          <DialogTitle>Changes requested</DialogTitle>
          <DialogDescription>
            Give feedback on “{taskTitle}” and send it back to the team.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <RichCommentEditor
            key={taskId ?? "none"}
            value={null}
            onChange={(doc, text) => {
              setRich(doc);
              setFeedback(text);
            }}
            onSubmit={submit}
            placeholder="What needs to change before this can be accepted?"
            pending={items}
            onAttach={add}
            onRemoveAttachment={remove}
            autoFocus
          />
        </div>
        <DialogFooter className="border-t border-border px-6 py-4">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} className="gap-2">
            {submitting || uploading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {submitting ? "Sending…" : "Uploading…"}
              </>
            ) : (
              "Send back"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
